/**
 * Detalhe do Orcamento — /orcamentos/:id (cockpit comercial, mockup Fase 2).
 *
 * Layout fiel ao mockup:
 *   breadcrumb · .eh (situacao + versao + acoes) · .kpi-strip-v2
 *   (Valor/Margem/Ambientes[m²·itens]/Validade/Probabilidade) + card
 *   "Próxima ação" · .row-2col
 *     - esquerda: Ambientes e itens (EditorAmbientes) + Composicao de preco
 *       + Anexos
 *     - direita (320px): Versoes (com Diff) + Timeline + blocos preservados
 *       (Identificacao/Cliente/Datas/Atendimentos/Observacoes/Contrato)
 *
 * Preserva 100% da maquina de estados e acoes existentes: enviar (com
 * Transition Form + cadastro de email), aprovar, recusar, expirar, nova
 * versao, converter em pedido, aplicar/salvar template, compartilhar (links
 * publicos), registrar razao de perda, excluir, recalcular precos.
 *
 * Dados: GET /api/orcamentos/{id} (fonte das acoes) + /cockpit (KPIs/versoes/
 * timeline, tolerante a falha) + /atendimentos.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArchive,
  FiBookmark,
  FiCheck,
  FiClipboard,
  FiCopy,
  FiEdit2,
  FiGitBranch,
  FiLayers,
  FiMail,
  FiRefreshCw,
  FiShare2,
  FiTrash2,
  FiX,
  FiZap,
} from "react-icons/fi";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  aprovarOrcamento,
  atualizarOrcamento,
  cancelarRevisaoOrcamento,
  converterEmPedido,
  criarNovaVersao,
  diffVersaoOrcamento,
  enviarOrcamento,
  excluirOrcamento,
  expirarOrcamento,
  finalizarRevisaoOrcamento,
  iniciarRevisaoOrcamento,
  obterCockpitOrcamento,
  obterOrcamento,
  recusarOrcamento,
  registrarRazaoPerda,
  RAZOES_PERDA,
} from "../../services/orcamentos";
import {
  aplicarTemplate,
  clonarTemplateDeOrcamento,
  listarTemplates,
} from "../../services/templatesOrcamento";
import {
  copiarLinkPublico,
  gerarLinkPublico,
  listarLinks,
  revogarLink,
} from "../../services/orcamentoLinkPublico";
import { PageContainer } from "../../styles/shared";

import AnexosBloco from "./AnexosBloco";
import ComposicaoPreco from "./ComposicaoPreco";
// Tarefa F2: select de forma de pagamento (condicao cadastrada) do DIVISOR.
import CondicaoPagamentoSelect from "./CondicaoPagamentoSelect";
import ContratoBloco from "./ContratoBloco";
// Motor DIVISOR (ADR-0050): composicao propria + cotacoes de terceiro.
// CASCATA continua com o ComposicaoPreco antigo — nada muda para ela.
import ComposicaoPrecoDivisor from "./motor/ComposicaoPrecoDivisor";
import ParceirosBloco from "./ParceirosBloco";
import CotacoesBloco from "./motor/CotacoesBloco";
// Orcamento v2 (mockup "Orcamento") — tabela de moveis + secoes da
// proposta + slide-over de import 3D. Substitui o EditorAmbientes antigo.
import AmbientesV2 from "./v2/AmbientesV2";
import Importar3DPanel from "./v2/Importar3DPanel";
import {
  CondicoesComerciais,
  ListasProposta,
  NotasDestaque,
  TotaisOrcamento,
} from "./v2/SecoesV2";
import {
  fmtData,
  fmtMoeda,
  PRIORIDADE_TONE,
  proximaDataUtilSugerida,
  SITUACAO_TONE_ORCAMENTO,
  STATUS_TEMPORAL_LABEL,
  STATUS_TEMPORAL_TONE,
} from "./helpers";
import { listarAtendimentos } from "../../services/atendimentos";
import { atualizarCliente } from "../../services/comercial";
import {
  Block,
  DangerButton,
  EmptyMsg,
  ErrorMsg,
  Field,
  FieldLabel,
  FieldRow,
  FieldValue,
  Form,
  FormFooter,
  InfoMsg,
  InlineHint,
  Input,
  Pill,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  Select,
  Textarea,
  WarningMsg,
} from "./styles";

const SITUACOES_EDITAVEIS = new Set(["RASCUNHO", "EM_NEGOCIACAO"]);

// ---------- Helpers de apresentacao ----------

function fmtCompacto(v) {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (n >= 1_000) return `R$ ${Math.round(n / 1_000)}k`;
  return `R$ ${Math.round(n)}`;
}

function labelTone(label) {
  if (label === "excelente" || label === "boa" || label === "alta") return "var(--success-text)";
  if (label === "ok" || label === "media") return "var(--warning-text)";
  if (label === "baixa") return "var(--danger-text)";
  return undefined;
}

function KpiV2({ label, value, unit, meta, metaTone }) {
  return (
    <div className="kpi-v2">
      <div className="lbl">{label}</div>
      <div className="val">{value}{unit ? <span className="unit">{unit}</span> : null}</div>
      <div className="meta" style={metaTone ? { color: metaTone } : undefined}>{meta}</div>
    </div>
  );
}

function OrcamentoDetalhe() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orcamentoId } = useParams();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const toast = useToast();

  // Item 6: ao voltar, retorna para a origem. Quando o orcamento foi aberto a
  // partir de um lead, o cockpit/dialogos passam `state.from` =
  // "/crm/leads/<leadId>" — assim "Voltar" cai de volta no cockpit. Sem
  // origem (deep-link, refresh), cai no board do CRM.
  const origemVolta =
    location.state?.from || window.history.state?.usr?.from || null;
  const voltarPara = origemVolta || "/crm";

  const [estado, setEstado] = useState({
    loading: true,
    error: null,
    orcamento: null,
  });
  const [cockpit, setCockpit] = useState(null);
  const [atendimentos, setAtendimentos] = useState([]);
  const [confirmando, setConfirmando] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [observacoes, setObservacoes] = useState("");
  // B7: codigo da razao de perda (acao em orcamentos RECUSADO/EXPIRADO).
  const [razaoCodigo, setRazaoCodigo] = useState("PRECO");
  const [salvando, setSalvando] = useState(false);
  const [acaoErro, setAcaoErro] = useState(null);
  // ADR-0050: qual ciencia o backend esta pedindo (campo `confirmacao_requerida`
  // da resposta 400). Decide qual checkbox mostrar sem depender de casar texto
  // da mensagem — o texto e escrito para o vendedor, nao para o parser.
  const [confirmacaoPedida, setConfirmacaoPedida] = useState(null);

  // Bloco F item 5: templates
  const [templatesDisponiveis, setTemplatesDisponiveis] = useState([]);
  const [templateSel, setTemplateSel] = useState("");
  const [nomeNovoTemplate, setNomeNovoTemplate] = useState("");
  const [descricaoNovoTemplate, setDescricaoNovoTemplate] = useState("");

  // Bloco G: links publicos
  const [linksPublicos, setLinksPublicos] = useState([]);
  const [diasValidadeLink, setDiasValidadeLink] = useState(30);
  const [copiado, setCopiado] = useState(null);
  // URL do link recem-gerado (token sai so na geracao/copia — C2/A11).
  const [linkGeradoUrl, setLinkGeradoUrl] = useState("");
  // Orcamento v2: slide-over "Importar projeto 3D".
  const [import3DAberto, setImport3DAberto] = useState(false);

  // Bloco H: padrao Transition Form — modal "Enviar" coleta follow-up
  const [proximaAcaoEnviar, setProximaAcaoEnviar] = useState("");
  const [dataProximaAcaoEnviar, setDataProximaAcaoEnviar] = useState("");
  // ADR-0050 (motor DIVISOR) — confirmacoes explicitas dos gates de envio:
  // G13 (cotacao ESTIMADA) e Q7 (cotacao FIRME vencida). Os checkboxes so
  // aparecem depois que o backend bloqueia pedindo a ciencia correspondente.
  const [aceitarEstimativas, setAceitarEstimativas] = useState(false);
  const [confirmarVencidas, setConfirmarVencidas] = useState(false);

  // Bloco F.4: modal "cadastrar email do cliente" antes de enviar.
  const [cadastrandoEmailCliente, setCadastrandoEmailCliente] = useState(false);
  const [aguardandoModalEnvio, setAguardandoModalEnvio] = useState(false);
  const [novoEmailCliente, setNovoEmailCliente] = useState("");
  const [novoTelefoneCliente, setNovoTelefoneCliente] = useState("");

  // Fase 3: diff entre versoes (painel sob demanda).
  const [diffVersaoNum, setDiffVersaoNum] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffErro, setDiffErro] = useState(null);

  const podeEditar = isMaster || hasAllPermissions(["orcamento:edit"]);
  const podeAprovar = isMaster || hasAllPermissions(["orcamento:aprovar"]);
  const podeRecusar = isMaster || hasAllPermissions(["orcamento:recusar"]);
  const podeConverter =
    isMaster ||
    hasAllPermissions(["orcamento:edit", "pedido:create"]);
  // Bloco F.6: action excluir orcamento (backend exige orcamento:delete + MFA recente)
  const podeExcluir = isMaster || hasAllPermissions(["orcamento:delete"]);

  const carregar = useCallback(async ({ silencioso = false } = {}) => {
    // Orcamento v2: `silencioso` evita o flash de "Carregando..." (e o
    // remount da arvore inteira) nos saves-on-blur das secoes da proposta.
    if (!silencioso) setEstado({ loading: true, error: null, orcamento: null });
    try {
      const [o, ck] = await Promise.all([
        obterOrcamento(request, orcamentoId),
        obterCockpitOrcamento(request, orcamentoId, { silentError: true }).catch(() => null),
      ]);
      setEstado({ loading: false, error: null, orcamento: o });
      setCockpit(ck);
      // Atendimentos vinculados (Bloco B item 7). Fail silencioso —
      // se o usuario nao tem atendimento:view, lista fica vazia.
      try {
        const ats = await listarAtendimentos(request, { orcamento: orcamentoId });
        const items = Array.isArray(ats) ? ats : ats?.results || [];
        setAtendimentos(items);
      } catch {
        setAtendimentos([]);
      }
    } catch (err) {
      setEstado({
        loading: false,
        error: err?.message || "Erro ao carregar.",
        orcamento: null,
      });
    }
  }, [request, orcamentoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const o = estado.orcamento;

  // SS3.A.6 (A17): abre o modal de envio assim que o email do cliente aparece
  // no payload recarregado (substitui o setTimeout que era race em rede lenta).
  useEffect(() => {
    if (aguardandoModalEnvio && o && (o.cliente_email || "").trim()) {
      setAguardandoModalEnvio(false);
      abrirConfirmacao("enviar");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aguardandoModalEnvio, o?.cliente_email]);

  const editavel = useMemo(
    () => Boolean(o && SITUACOES_EDITAVEIS.has(o.situacao) && podeEditar),
    [o, podeEditar],
  );

  // Discriminador do motor de preco (ADR-0050): vem do serializer do
  // orcamento. DIVISOR troca a composicao e liga areas/insumos/cotacoes.
  const motorDivisor = o?.motor_preco === "DIVISOR";

  // Orcamento v2: reload sem flash (saves-on-blur de ambientes/moveis/secoes).
  const recarregar = useCallback(
    () => carregar({ silencioso: true }),
    [carregar],
  );

  // Orcamento v2: PATCH parcial dos campos da proposta (save-on-blur das
  // secoes Condicoes/Observacoes/Totais/Notas) + reload silencioso.
  const patchOrcamento = useCallback(
    async (campos) => {
      await atualizarOrcamento(request, orcamentoId, campos);
      await carregar({ silencioso: true });
    },
    [request, orcamentoId, carregar],
  );

  // ---------- Acoes ----------

  const abrirConfirmacao = (tipo) => {
    // Bloco F.4: intercepta "enviar" sem email — abre modal de cadastro
    // de email primeiro. Apos salvar, abrirConfirmacao("enviar") eh
    // chamado de novo automaticamente.
    if (tipo === "enviar" && o && !(o.cliente_email || "").trim()) {
      setNovoEmailCliente("");
      setNovoTelefoneCliente(o.cliente_telefone || "");
      setCadastrandoEmailCliente(true);
      return;
    }
    setConfirmando(tipo);
    setMotivo("");
    setObservacoes("");
    setAcaoErro(null);
    setConfirmacaoPedida(null);
    // Bloco H: pre-preenche campos do Transition Form "Enviar"
    if (tipo === "enviar" && o) {
      // ADR-0050: cada abertura do modal zera as ciencias dos gates G13/Q7.
      setAceitarEstimativas(false);
      setConfirmarVencidas(false);
      setProximaAcaoEnviar(
        o.proxima_acao && o.proxima_acao.trim()
          ? o.proxima_acao
          : "Confirmar recebimento e tirar dúvidas"
      );
      setDataProximaAcaoEnviar(
        o.data_proxima_acao || proximaDataUtilSugerida(3)
      );
    }
  };

  // Traduz o `status_email` que o backend devolve no envio. O orcamento
  // transiciona de qualquer jeito (o disparo e best-effort), entao o unico
  // jeito do vendedor saber se o cliente REALMENTE recebeu e este aviso.
  const avisarSobreEmail = (status) => {
    if (!status || status === "enviado") {
      toast.success("Orçamento enviado — e-mail a caminho do cliente.");
      return;
    }
    const MOTIVO = {
      sem_configuracao:
        "e-mail automático não está configurado (Administração → "
        + "Integrações → E-mail).",
      notificacao_desativada:
        "o aviso de orçamento enviado está desligado na configuração de e-mail.",
      cliente_sem_email: "este cliente não tem e-mail cadastrado.",
      simulado_homologacao:
        "a configuração de e-mail está em HOMOLOGAÇÃO — o envio foi simulado, "
        + "nada saiu de verdade.",
      falhou: "houve uma falha no disparo (o histórico de e-mails tem o detalhe).",
      sem_unidade: "o orçamento está sem unidade de negócio.",
    };
    toast.warning(
      "Orçamento marcado como enviado, mas o cliente NÃO recebeu e-mail: "
      + (MOTIVO[status] || status)
      + " Use 'Compartilhar link' para mandar por WhatsApp.",
    );
  };

  // Bloco F.4: salva email do cliente e re-abre fluxo de envio.
  const salvarEmailCliente = async () => {
    const email = novoEmailCliente.trim();
    if (!email) {
      setAcaoErro("Email é obrigatório.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAcaoErro("Email parece inválido. Verifique antes de salvar.");
      return;
    }
    try {
      await atualizarCliente(request, o.cliente, {
        email,
        telefone: novoTelefoneCliente.trim() || undefined,
      });
      toast.success("Email do cliente cadastrado.");
      setCadastrandoEmailCliente(false);
      setNovoEmailCliente("");
      setNovoTelefoneCliente("");
      setAcaoErro(null);
      await carregar();
      setAguardandoModalEnvio(true);
    } catch (err) {
      setAcaoErro(err?.message || "Erro ao salvar email.");
    }
  };

  const fecharConfirmacao = () => {
    setConfirmando(null);
    setMotivo("");
    setObservacoes("");
    setAcaoErro(null);
    setConfirmacaoPedida(null);
    setLinkGeradoUrl("");
  };

  const executarAcao = async () => {
    if (!o) return;
    setSalvando(true);
    setAcaoErro(null);
    try {
      if (confirmando === "enviar") {
        // Bloco H: Transition Form — envia body com follow-up
        if (!proximaAcaoEnviar.trim()) {
          setAcaoErro("Defina a 'próxima ação' (o follow-up após enviar).");
          setSalvando(false);
          return;
        }
        if (!dataProximaAcaoEnviar) {
          setAcaoErro("Defina a 'data prevista' do follow-up.");
          setSalvando(false);
          return;
        }
        const enviado = await enviarOrcamento(request, o.id, {
          proxima_acao: proximaAcaoEnviar.trim(),
          data_proxima_acao: dataProximaAcaoEnviar,
          // ADR-0050: ciencias explicitas dos gates do motor DIVISOR (G13/Q7).
          aceitar_estimativas_internas: motorDivisor && aceitarEstimativas,
          confirmar_cotacoes_vencidas: motorDivisor && confirmarVencidas,
        });
        // O orcamento transiciona mesmo quando o email nao sai (hook e
        // best-effort). Sem avisar aqui, o vendedor acha que o cliente
        // recebeu — foi exatamente o que aconteceu em producao.
        avisarSobreEmail(enviado?.status_email);
      } else if (confirmando === "aprovar") {
        await aprovarOrcamento(request, o.id);
      } else if (confirmando === "recusar") {
        if (!motivo.trim()) {
          setAcaoErro("Motivo é obrigatório.");
          setSalvando(false);
          return;
        }
        await recusarOrcamento(request, o.id, motivo);
      } else if (confirmando === "expirar") {
        await expirarOrcamento(request, o.id);
      } else if (confirmando === "registrar-razao") {
        if (!razaoCodigo) {
          setAcaoErro("Selecione uma razão.");
          setSalvando(false);
          return;
        }
        await registrarRazaoPerda(request, o.id, {
          codigo: razaoCodigo,
          descricao: motivo,
        });
      } else if (confirmando === "nova-versao") {
        await criarNovaVersao(request, o.id, motivo);
      } else if (confirmando === "iniciar-revisao") {
        await iniciarRevisaoOrcamento(request, o.id);
      } else if (confirmando === "finalizar-revisao") {
        await finalizarRevisaoOrcamento(request, o.id);
      } else if (confirmando === "cancelar-revisao") {
        await cancelarRevisaoOrcamento(request, o.id);
      } else if (confirmando === "converter") {
        const pedido = await converterEmPedido(request, o.id, observacoes);
        fecharConfirmacao();
        navigate(`/pedidos/${pedido.id}`);
        return;
      } else if (confirmando === "aplicar-template") {
        if (!templateSel) {
          setAcaoErro("Selecione um template.");
          setSalvando(false);
          return;
        }
        await aplicarTemplate(request, templateSel, o.id);
      } else if (confirmando === "salvar-como-template") {
        if (!nomeNovoTemplate.trim()) {
          setAcaoErro("Nome do template é obrigatório.");
          setSalvando(false);
          return;
        }
        await clonarTemplateDeOrcamento(request, o.id, {
          nome: nomeNovoTemplate,
          descricao: descricaoNovoTemplate,
        });
      } else if (confirmando === "excluir") {
        await excluirOrcamento(request, o.id);
        toast.success(`Orcamento ${o.numero} excluido.`);
        fecharConfirmacao();
        navigate(voltarPara);
        return;
      } else if (confirmando === "compartilhar") {
        const dias = Number(diasValidadeLink) || 30;
        if (dias < 1 || dias > 365) {
          setAcaoErro("Dias de validade deve estar entre 1 e 365.");
          setSalvando(false);
          return;
        }
        const novo = await gerarLinkPublico(request, o.id, { diasValidade: dias });
        // C2/A11: o token sai SO na resposta da geracao (e no copiar). Guarda
        // a URL completa e copia automaticamente — sem montar `.../{token}`
        // a partir da listagem (que nao expoe token => virava `.../undefined`).
        if (novo?.url) {
          setLinkGeradoUrl(novo.url);
          try {
            await navigator.clipboard.writeText(novo.url);
            toast.success("Link gerado e copiado.");
          } catch {
            toast.success("Link gerado.");
          }
        }
        const payload = await listarLinks(request, o.id);
        const items = Array.isArray(payload) ? payload : payload?.results || [];
        setLinksPublicos(items);
        setSalvando(false);
        return; // nao fecha o panel — usuario continua vendo o link gerado
      }
      await carregar();
      fecharConfirmacao();
    } catch (err) {
      setAcaoErro(err?.message || "Erro na operação.");
      setConfirmacaoPedida(err?.payload?.confirmacao_requerida || null);
    } finally {
      setSalvando(false);
    }
  };

  // Carrega templates disponiveis quando abre o painel "aplicar template".
  useEffect(() => {
    if (confirmando !== "aplicar-template") return;
    listarTemplates(request, { ativo: true, pageSize: 50 })
      .then((payload) => {
        const items = Array.isArray(payload) ? payload : payload?.results || [];
        setTemplatesDisponiveis(items);
        if (items.length > 0 && !templateSel) {
          setTemplateSel(items[0].id);
        }
      })
      .catch(() => setTemplatesDisponiveis([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmando, request]);

  // Bloco G: carrega links publicos quando abre painel "compartilhar"
  useEffect(() => {
    if (confirmando !== "compartilhar" || !o?.id) return;
    listarLinks(request, o.id)
      .then((payload) => {
        const items = Array.isArray(payload) ? payload : payload?.results || [];
        setLinksPublicos(items);
      })
      .catch(() => setLinksPublicos([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmando, request, o?.id]);

  const podeCompartilhar = isMaster || hasAllPermissions(["orcamento:gerar_link_publico"]);

  const handleRevogarLink = async (linkId) => {
    if (!window.confirm("Revogar este link? Cliente não poderá mais acessar.")) return;
    try {
      await revogarLink(request, linkId);
      const payload = await listarLinks(request, o.id);
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setLinksPublicos(items);
    } catch (err) {
      setAcaoErro(err?.message || "Erro ao revogar.");
    }
  };

  const handleCopiarLink = async (linkId) => {
    let url;
    try {
      // Endpoint dedicado auditado — token nao vem na listagem (C2/A11).
      ({ url } = await copiarLinkPublico(request, linkId));
    } catch (err) {
      toast.error(err?.message || "Erro ao obter o link.");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(linkId);
      setTimeout(() => setCopiado(null), 2000);
    } catch (_) {
      window.prompt("Copie o link abaixo:", url);
    }
  };

  // Fase 3: abre o painel de diff entre a versao N e a anterior.
  const abrirDiff = async (numero) => {
    if (!o) return;
    setDiffVersaoNum(numero);
    setDiffLoading(true);
    setDiffErro(null);
    setDiffData(null);
    try {
      const d = await diffVersaoOrcamento(request, o.id, numero, { silentError: true });
      setDiffData(d);
    } catch (err) {
      setDiffErro(err?.message || "Erro ao calcular o diff.");
    } finally {
      setDiffLoading(false);
    }
  };

  const fecharDiff = () => {
    setDiffVersaoNum(null);
    setDiffData(null);
    setDiffErro(null);
  };

  const actionsKebab = useMemo(() => {
    if (!o) return [];
    const out = [];
    if (editavel) {
      if (o.situacao === "RASCUNHO") {
        // Bloco E.2 v2 (ADR-0019) D9: bloqueia enviar quando ha pendencias CAD
        const totalBloqueando = Number(
          o.precificacao_status?.total_bloqueando || 0,
        );
        const enviarBloqueado = totalBloqueando > 0;
        out.push({
          label: enviarBloqueado
            ? `Enviar (bloqueado: ${totalBloqueando} pendente${totalBloqueando > 1 ? "s" : ""})`
            : "Enviar ao cliente",
          icon: <FiMail size={14} />,
          onClick: enviarBloqueado
            ? () => toast.error(
                "Existem items aguardando precificação pelo estoque. "
                + "Cadastre os produtos pendentes ou clique 'Atualizar preços' "
                + "no editor antes de enviar."
              )
            : () => abrirConfirmacao("enviar"),
          disabled: enviarBloqueado,
          key: "send",
        });
      }
      if (o.situacao === "EM_NEGOCIACAO") {
        out.push({
          label: "Criar nova versão",
          icon: <FiCopy size={14} />,
          onClick: () => abrirConfirmacao("nova-versao"),
          key: "ver",
        });
      }
    }
    if (podeAprovar && o.situacao === "EM_NEGOCIACAO") {
      out.push({
        label: "Aprovar (cliente OK)",
        icon: <FiCheck size={14} />,
        onClick: () => abrirConfirmacao("aprovar"),
        key: "appr",
      });
    }
    if (podeRecusar && ["EM_NEGOCIACAO", "APROVADO"].includes(o.situacao)) {
      out.push({
        label: "Recusar",
        icon: <FiX size={14} />,
        onClick: () => abrirConfirmacao("recusar"),
        danger: true,
        key: "rec",
      });
    }
    if (editavel && ["RASCUNHO", "EM_NEGOCIACAO"].includes(o.situacao)) {
      out.push({
        label: "Marcar expirado",
        icon: <FiArchive size={14} />,
        onClick: () => abrirConfirmacao("expirar"),
        danger: true,
        key: "exp",
      });
    }
    // B7 (ADR-0037): registrar razao de perda em orcamentos perdidos.
    if (podeEditar && ["RECUSADO", "EXPIRADO"].includes(o.situacao)) {
      out.push({
        label: "Registrar razão de perda",
        icon: <FiClipboard size={14} />,
        onClick: () => abrirConfirmacao("registrar-razao"),
        key: "razao-perda",
      });
    }
    // Bloco F item 5: templates
    if (editavel) {
      out.push({
        label: "Aplicar template",
        icon: <FiLayers size={14} />,
        onClick: () => abrirConfirmacao("aplicar-template"),
        key: "tpl-aplicar",
      });
    }
    if (o.versao_ativa?.ambientes?.length > 0) {
      out.push({
        label: "Salvar como template",
        icon: <FiBookmark size={14} />,
        onClick: () => abrirConfirmacao("salvar-como-template"),
        key: "tpl-salvar",
      });
    }
    // Bloco G + H + F.5: compartilhar com cliente (link publico).
    if (podeCompartilhar && !["CONVERTIDO", "RECUSADO", "EXPIRADO"].includes(o.situacao)) {
      out.push({
        label: o.lead ? "Compartilhar com lead" : "Compartilhar com cliente",
        icon: <FiShare2 size={14} />,
        onClick: () => abrirConfirmacao("compartilhar"),
        key: "link-publico",
      });
    }
    // Bloco F.6: excluir orcamento. Gate backend: orcamento:delete + MFA recente.
    if (podeExcluir) {
      out.push({
        label: "Excluir orçamento",
        icon: <FiTrash2 size={14} />,
        onClick: () => abrirConfirmacao("excluir"),
        danger: true,
        key: "del",
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o, editavel, podeAprovar, podeRecusar, podeCompartilhar, podeExcluir]);

  const primaryAction = useMemo(() => {
    if (!o) return null;
    if (o.situacao === "APROVADO" && podeConverter) {
      return {
        label: "Converter em pedido",
        icon: <FiZap size={14} />,
        onClick: () => abrirConfirmacao("converter"),
      };
    }
    // Orcamento v2 (mockup): em edicao, "Enviar ao cliente" eh o botao
    // primario do header. Mesma defesa D9 do item do kebab (pendencias CAD).
    if (editavel) {
      const totalBloqueando = Number(o.precificacao_status?.total_bloqueando || 0);
      return {
        label: "Enviar ao cliente",
        icon: <FiMail size={14} />,
        onClick: totalBloqueando > 0
          ? () => toast.error(
              "Existem items aguardando precificação pelo estoque. "
              + "Cadastre os produtos pendentes ou clique 'Atualizar preços' "
              + "antes de enviar."
            )
          : () => abrirConfirmacao("enviar"),
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o, podeConverter, editavel]);

  if (estado.loading) {
    return (
      <PageContainer>
        <EmptyMsg>Carregando orçamento...</EmptyMsg>
      </PageContainer>
    );
  }
  if (estado.error || !o) {
    return (
      <PageContainer>
        <ErrorMsg>{estado.error || "Orçamento não encontrado."}</ErrorMsg>
        <SecondaryButton type="button" onClick={() => navigate(voltarPara)}>
          Voltar
        </SecondaryButton>
      </PageContainer>
    );
  }

  const ck = cockpit;
  const kp = ck?.kpis;
  const cab = ck?.orcamento;
  const versoes = ck?.versoes || [];
  const timeline = ck?.timeline || [];
  const proximaAcaoCard = ck?.proxima_acao || null;
  // Origem CRM: contato eh "lead", nao "cliente" (terminologia do compartilhar).
  const ehLead = Boolean(o.lead);
  const termoContato = ehLead ? "lead" : "cliente";
  const qtdAmbientes = kp?.ambientes_count ?? (o.versao_ativa?.ambientes?.length || 0);

  // Subtitulo composto (mockup): cliente · projeto · vendedor · valor · margem · criado
  const subParts = [];
  if (o.nome_projeto) subParts.push(o.nome_projeto);
  else if (cab?.cliente_email) subParts.push(cab.cliente_email);
  if (o.vendedor_nome) subParts.push(o.vendedor_nome);
  subParts.push(fmtMoeda(o.valor_total));
  if (cab?.margem != null) subParts.push(`margem ${Number(cab.margem).toFixed(1)}%`);
  if (cab?.criado_rotulo) subParts.push(`criado ${cab.criado_rotulo}`);

  return (
    <PageContainer style={{ maxWidth: "none" }} className="orc2">
      <div className="orc-wrap">
      <div className="breadcrumb" style={{ marginBottom: 8 }}>
        <span className="crumb">Início</span><span className="crumb-sep">/</span>
        <span className="crumb">Comercial</span><span className="crumb-sep">/</span>
        <span className="crumb">Orçamentos</span><span className="crumb-sep">/</span>
        <span className="crumb active">{o.numero}</span>
      </div>

      <EntityHeader
        onBack={() => navigate(voltarPara)}
        backLabel={origemVolta ? "Voltar" : "CRM"}
        title={o.numero}
        subtitle={`${o.cliente_nome}${subParts.length ? ` · ${subParts.join(" · ")}` : ""}`}
        badges={[
          {
            label: o.situacao_display,
            tone: SITUACAO_TONE_ORCAMENTO[o.situacao] || "neutral",
            key: "sit",
          },
          ...(o.status_temporal ? [{
            label: STATUS_TEMPORAL_LABEL[o.status_temporal],
            tone: STATUS_TEMPORAL_TONE[o.status_temporal] || "neutral",
            key: "tempo",
          }] : []),
          {
            label: o.prioridade_display,
            tone: PRIORIDADE_TONE[o.prioridade] || "neutral",
            key: "pri",
          },
          { label: `v${o.versao_ativa?.numero_versao ?? "—"}`, tone: "neutral", key: "ver" },
        ]}
        primaryAction={primaryAction || undefined}
        actions={actionsKebab}
      />

      {/* KPIs (Orcamento v2): valor c/ desconto, desconto, tributos NF,
          margem alvo, ambientes + card "próxima ação" */}
      <div className="kpi-strip-v2 standalone">
        <KpiV2
          label={o.motor_preco === "DIVISOR" ? "Valor ao cliente" : "Valor (c/ desconto)"}
          value={fmtCompacto(
            o.motor_preco === "DIVISOR" ? o.valor_total : o.valor_total_final,
          )}
          meta={o.motor_preco === "DIVISOR"
            ? "preço definido no motor divisor"
            : "após desconto concedido"}
        />
        {/* Desconto, tributos e margem de lucro sao conceitos da CASCATA.
            Sob o motor DIVISOR eles nao existem (quem classifica o preco e a
            alcada) e exibi-los mostrava numeros de outra regua — inclusive
            um imposto diferente do que a Composicao do motor calcula
            (auditoria 2026-08-07, V13/M-ORC-5). No divisor a fonte unica
            desses numeros e o bloco "Composicao do preco" logo abaixo. */}
        {o.motor_preco !== "DIVISOR" && (
          <>
            <KpiV2
              label="Desconto"
              value={Number(o.desconto_percentual || 0) > 0
                ? Number(o.desconto_percentual)
                : "—"}
              unit={Number(o.desconto_percentual || 0) > 0 ? "%" : undefined}
              meta={(() => {
                const sub = Number(o.valor_apos_rt || 0) + Number(o.valor_manual_total || 0);
                const dif = sub - Number(o.valor_total_final || 0);
                return dif > 0 ? `− ${fmtMoeda(dif)}` : "sem desconto";
              })()}
            />
            <KpiV2
              label="Valor c/ tributos (NF)"
              value={o.emitir_nf ? fmtCompacto(o.valor_com_tributos) : "—"}
              meta={o.emitir_nf
                ? `${Number(o.tributos_percentual || 0)}% sobre o liquido`
                : "NF desativada"}
            />
          </>
        )}
        <KpiV2
          label={o.motor_preco === "DIVISOR" ? "Margem líquida alvo" : "Margem alvo"}
          value={o.motor_preco === "DIVISOR"
            ? (kp?.margem ?? "—")
            : Number(o.margem_lucro_percentual || 0)}
          unit={o.motor_preco === "DIVISOR" && kp?.margem == null ? undefined : "%"}
          meta={o.motor_preco === "DIVISOR"
            ? (o.emitir_nf ? "com NF" : "sem NF")
            : (kp?.margem_label || "—")}
          metaTone={o.motor_preco === "DIVISOR" ? undefined : labelTone(kp?.margem_label)}
        />
        <KpiV2
          label="Ambientes"
          value={qtdAmbientes}
          meta={kp ? `${kp.itens_count} ${kp.itens_count === 1 ? "móvel" : "móveis"}` : "—"}
        />
        {proximaAcaoCard && (
          <div className="next-action">
            <div className="lbl"><FiZap aria-hidden="true" /> PRÓXIMA AÇÃO</div>
            <div className="title">
              {proximaAcaoCard.texto}
              {proximaAcaoCard.data ? ` — ${fmtData(proximaAcaoCard.data)}` : ""}
              {proximaAcaoCard.atrasada ? " (atrasado)" : ""}
            </div>
            <button
              type="button"
              className="action-link"
              onClick={() => navigate(proximaAcaoCard.lead_id ? `/crm/leads/${proximaAcaoCard.lead_id}` : "/crm")}
            >
              Abrir CRM →
            </button>
          </div>
        )}
      </div>

      {o.situacao === "CONVERTIDO" && (
        <InfoMsg style={{ marginTop: 14 }}>
          <FiCheck size={14} aria-hidden="true" />
          Este orçamento já foi convertido em pedido. Edição bloqueada.
        </InfoMsg>
      )}
      {o.situacao === "RECUSADO" && (
        <WarningMsg style={{ marginTop: 14 }}>
          <FiX size={14} aria-hidden="true" />
          Recusado{o.motivo_recusa && ` — ${o.motivo_recusa}`}
        </WarningMsg>
      )}
      {o.situacao === "EXPIRADO" && (
        <WarningMsg style={{ marginTop: 14 }}>
          <FiArchive size={14} aria-hidden="true" />
          Orçamento expirado. Para retomar, crie um novo.
        </WarningMsg>
      )}

      {/* Revisao pedida pelo lead (pendente) — aviso + iniciar */}
      {o.revisao_solicitada_em && !o.revisao_em_andamento && (
        <WarningMsg style={{ marginTop: 14, flexWrap: "wrap", gap: 10 }}>
          <FiRefreshCw size={14} aria-hidden="true" />
          <span style={{ flex: 1, minWidth: 220 }}>
            O {termoContato} pediu revisão em <strong>{fmtData(o.revisao_solicitada_em, true)}</strong>
            {o.revisao_solicitada_motivo ? <>: “{o.revisao_solicitada_motivo}”</> : "."}
          </span>
          {editavel && (
            <PrimaryButton
              type="button"
              onClick={() => abrirConfirmacao("iniciar-revisao")}
              style={{ padding: "6px 12px" }}
            >
              <FiRefreshCw size={13} /> Iniciar revisão
            </PrimaryButton>
          )}
        </WarningMsg>
      )}

      {/* Revisao em andamento — finalizar / cancelar */}
      {o.revisao_em_andamento && (
        <InfoMsg style={{ marginTop: 14, flexWrap: "wrap", gap: 10 }}>
          <FiRefreshCw size={14} aria-hidden="true" />
          <span style={{ flex: 1, minWidth: 220 }}>
            Revisão em andamento (v{o.versao_ativa?.numero_versao}). Edite os ambientes/itens
            e finalize; ao enviar/compartilhar, o {termoContato} recebe a nova versão.
          </span>
          <SecondaryButton
            type="button"
            onClick={() => abrirConfirmacao("finalizar-revisao")}
            style={{ padding: "6px 12px" }}
          >
            <FiCheck size={13} /> Finalizar revisão
          </SecondaryButton>
          <DangerButton
            type="button"
            onClick={() => abrirConfirmacao("cancelar-revisao")}
            style={{ padding: "6px 12px" }}
          >
            <FiX size={13} /> Cancelar
          </DangerButton>
        </InfoMsg>
      )}

      {/* Orcamento v2: notas em destaque exibidas ao cliente (banners) */}
      <div style={{ marginTop: 14 }}>
        <NotasDestaque orcamento={o} editavel={editavel} onPatch={patchOrcamento} />
      </div>

      {/* 2 colunas (mockup): conteudo principal largo + sidebar 332px */}
      <div className="orc-grid">
        {/* Esquerda: ambientes + totais + condicoes + observacoes + composicao + anexos */}
        <div className="stack-14">
          {/* Orcamento v2: tabela de moveis fiel ao mockup + totais/NF */}
          <div>
            <AmbientesV2
              orcamento={o}
              editavel={editavel}
              onChange={recarregar}
              onAbrirImport3D={() => setImport3DAberto(true)}
            />
            <TotaisOrcamento
              orcamento={o}
              editavel={editavel}
              onPatch={patchOrcamento}
            />
          </div>

          <CondicoesComerciais
            orcamento={o}
            editavel={editavel}
            onPatch={patchOrcamento}
            // Tarefa F2: no motor DIVISOR o select de condicao cadastrada
            // entra acima do texto livre (que vira observacao da proposta).
            seletorFormaPagamento={motorDivisor ? (
              <CondicaoPagamentoSelect orcamento={o} editavel={editavel} />
            ) : null}
          />

          <ListasProposta
            orcamento={o}
            editavel={editavel}
            onPatch={patchOrcamento}
          />

          {/* ADR-0050: bloco "Terceiros e servicos" (CotacaoTerceiro) no nivel
              do orcamento — so no motor DIVISOR. */}
          {motorDivisor && (
            <div className="block">
              <div className="block__body">
                <CotacoesBloco
                  orcamento={o}
                  ambientes={o.versao_ativa?.ambientes || []}
                  editavel={editavel}
                  onChange={recarregar}
                />
              </div>
            </div>
          )}

          {/* Composicao do preco final: DIVISOR usa o componente do motor novo
              (ADR-0050); CASCATA continua com o antigo (ADR-0019) intacto. */}
          {motorDivisor ? (
            <ComposicaoPrecoDivisor
              orcamento={o}
              editavel={editavel}
              onChange={recarregar}
            />
          ) : (
            <ComposicaoPreco
              orcamento={o}
              editavel={editavel}
              onChange={carregar}
            />
          )}

          {/* M-ORC-1: os parceiros só podiam ser escolhidos na CRIAÇÃO do
              orçamento — trocar o arquiteto ou corrigir um RT digitado
              errado exigia refazer a proposta. Fica logo abaixo da
              composição porque o RT deles entra nela. */}
          <ParceirosBloco
            orcamento={o}
            editavel={editavel}
            onAlterado={recarregar}
          />

          {/* Bloco F item 4: Anexos (mockup posiciona na coluna principal) */}
          <AnexosBloco orcamentoId={o.id} editavel={editavel} />
        </div>

        {/* Direita (ordem do mockup): Identificacao, Cliente & projeto,
            Versoes, Timeline + blocos preservados */}
        <div className="stack-14">
          <div className="block">
            <div className="block__head"><div className="block__title">Identificação</div></div>
            <div className="block__body">
              <dl className="kv">
                <dt>Número</dt><dd className="mono">{o.numero}</dd>
                <dt>Unidade</dt><dd>{o.unidade_codigo}</dd>
                <dt>Versão</dt><dd>v{o.versao_ativa?.numero_versao ?? "—"}</dd>
                <dt>Situação</dt>
                <dd><span className={`badge ${SITUACAO_TONE_ORCAMENTO[o.situacao] || "neutral"} no-dot`}>{o.situacao_display}</span></dd>
                <dt>Prioridade</dt>
                <dd><span className={`badge ${PRIORIDADE_TONE[o.prioridade] || "neutral"} no-dot`}>{o.prioridade_display}</span></dd>
                <dt>Emissão</dt><dd>{fmtData(o.created_at)}</dd>
                {/* `valor_total` despacha por motor no backend (DIVISOR soma
                    preco_alocado; cascata devolve valor_total_final) — lendo
                    valor_total_final direto, o card mostrava R$ 0,00 ao lado
                    de ambientes somando dezenas de milhares. */}
                <dt>Valor</dt><dd className="accent">{fmtMoeda(o.valor_total)}</dd>
              </dl>
            </div>
          </div>

          <div className="block">
            <div className="block__head">
              <div className="block__title">Cliente &amp; projeto</div>
              <Link to={`/clientes/${o.cliente}`} className="btn btn--ghost btn--sm">
                <FiEdit2 size={12} aria-hidden="true" /> Editar
              </Link>
            </div>
            <div className="block__body">
              <dl className="kv">
                <dt>Contratante</dt><dd>{o.cliente_nome}</dd>
                <dt>Telefone</dt><dd className="mono">{o.cliente_telefone || "—"}</dd>
                <dt>E-mail</dt><dd className="mono">{o.cliente_email || "—"}</dd>
                <dt>Projeto</dt><dd>{o.nome_projeto || "—"}</dd>
                <dt>Vendedor</dt><dd>{o.vendedor_nome || "—"}</dd>
              </dl>
            </div>
          </div>

          <div className="block">
            <div className="block__head">
              <div className="block__title">Versões</div>
              {editavel && o.situacao === "EM_NEGOCIACAO" && (
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => abrirConfirmacao("nova-versao")}>
                  <FiCopy size={12} /> Nova v
                </button>
              )}
            </div>
            <div className="block__body">
              {versoes.length === 0 ? (
                <EmptyMsg style={{ padding: 8 }}>Sem versões.</EmptyMsg>
              ) : (
                <ul className="versions">
                  {versoes.map((v) => (
                    <li key={v.numero_versao}>
                      <span className={`ver-tag ${v.is_atual ? "current" : ""}`}>v{v.numero_versao}</span>
                      <div className="ver-meta">
                        <div className="who" title={v.autor || ""}>{v.autor || "—"}</div>
                        <div className="when">{v.rotulo}{v.is_atual ? " · atual" : ""}</div>
                      </div>
                      <button
                        type="button"
                        className="ver-link"
                        disabled={v.numero_versao <= 1}
                        title={v.numero_versao <= 1 ? "Primeira versão — sem anterior para comparar" : "Comparar com a versão anterior"}
                        onClick={() => abrirDiff(v.numero_versao)}
                      >
                        Diff
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {timeline.length > 0 && (
            <div className="block">
              <div className="block__head"><div className="block__title">Timeline</div></div>
              <div className="block__body" style={{ padding: "8px 18px 16px" }}>
                <div className="timeline-audit timeline-audit--wide" style={{ border: 0, padding: 0, background: "transparent" }}>
                  {timeline.map((e, i) => {
                    const [dataDia, dataHora] = (e.data_label || "").split(" ");
                    return (
                      <div key={i} className="timeline-audit__event" data-tone={e.tone}>
                        <div className="time">
                          <div>{dataDia}</div>
                          {dataHora && <div>{dataHora}</div>}
                        </div>
                        <div className="dot-col"><div className="dot" /></div>
                        <div className="body">
                          <span className="actor">{e.ator || "Sistema"}</span>{" "}
                          <span className="action">{e.acao}</span>
                          {e.meta && <div className="meta">{e.meta}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <Block>
            <SectionTitle>Datas</SectionTitle>
            <FieldRow><FieldLabel>Criado em</FieldLabel><FieldValue>{fmtData(o.created_at, true)}</FieldValue></FieldRow>
            {o.enviado_em && (
              <FieldRow><FieldLabel>Enviado em</FieldLabel><FieldValue>{fmtData(o.enviado_em, true)}</FieldValue></FieldRow>
            )}
            {o.aprovado_em && (
              <FieldRow><FieldLabel>Aprovado em</FieldLabel><FieldValue>{fmtData(o.aprovado_em, true)}</FieldValue></FieldRow>
            )}
            {o.recusado_em && (
              <FieldRow><FieldLabel>Recusado em</FieldLabel><FieldValue>{fmtData(o.recusado_em, true)}</FieldValue></FieldRow>
            )}
            {o.convertido_em && (
              <FieldRow><FieldLabel>Convertido em</FieldLabel><FieldValue>{fmtData(o.convertido_em, true)}</FieldValue></FieldRow>
            )}
            {o.expirado_em && (
              <FieldRow><FieldLabel>Expirado em</FieldLabel><FieldValue>{fmtData(o.expirado_em, true)}</FieldValue></FieldRow>
            )}
            <FieldRow><FieldLabel>Validade</FieldLabel><FieldValue>{o.validade_dias} dias</FieldValue></FieldRow>
          </Block>

          {atendimentos.length > 0 && (
            <Block>
              <SectionTitle>Atendimentos vinculados ({atendimentos.length})</SectionTitle>
              {atendimentos.slice(0, 5).map((a) => (
                <FieldRow key={a.id}>
                  <FieldLabel>{fmtData(a.data_atendimento)}</FieldLabel>
                  <FieldValue>
                    <strong>{a.assunto}</strong>
                    <InlineHint style={{ display: "block", marginTop: 2 }}>
                      {a.tipo_display} · {a.canal_display}
                      {a.resultado_display && ` · ${a.resultado_display}`}
                    </InlineHint>
                  </FieldValue>
                </FieldRow>
              ))}
              {atendimentos.length > 5 && (
                <InlineHint>
                  +{atendimentos.length - 5} atendimento(s). Veja a lista completa filtrada por este orçamento.
                </InlineHint>
              )}
            </Block>
          )}

          {o.observacoes && (
            <Block>
              <SectionTitle>Observações</SectionTitle>
              <FieldValue style={{ whiteSpace: "pre-wrap" }}>{o.observacoes}</FieldValue>
            </Block>
          )}

          {/* Bloco F.8: Contratos (gerar/listar). So aparece em APROVADO ou
              quando ja existem contratos vinculados. */}
          <ContratoBloco orcamento={o} onChange={carregar} />
        </div>
      </div>
      </div>

      {/* SlideOverPanel — confirmacao de acoes */}
      <SlideOverPanel
        open={Boolean(confirmando)}
        onClose={fecharConfirmacao}
        title={
          confirmando === "enviar" ? "Enviar ao cliente"
          : confirmando === "aprovar" ? "Aprovar orçamento"
          : confirmando === "recusar" ? "Recusar orçamento"
          : confirmando === "expirar" ? "Marcar como expirado"
          : confirmando === "nova-versao" ? "Criar nova versão"
          : confirmando === "iniciar-revisao" ? "Iniciar revisão"
          : confirmando === "finalizar-revisao" ? "Finalizar revisão"
          : confirmando === "cancelar-revisao" ? "Cancelar revisão"
          : confirmando === "converter" ? "Converter em pedido"
          : confirmando === "aplicar-template" ? "Aplicar template"
          : confirmando === "salvar-como-template" ? "Salvar como template"
          : confirmando === "compartilhar" ? (ehLead ? "Compartilhar com lead" : "Compartilhar com cliente")
          : confirmando === "excluir" ? "Excluir orçamento"
          : confirmando === "registrar-razao" ? "Registrar razão de perda"
          : ""
        }
        subtitle={o.numero}
        width="min(90vw, 480px)"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            executarAcao();
          }}
        >
          {confirmando === "enviar" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Marca o orçamento como <strong>em negociação</strong> e registra a data de envio.
                A edição continua liberada — qualquer mudança depois disto gera uma nova versão.
              </p>
              <p style={{ fontSize: "0.85rem", color: "#666" }}>
                Defina o <strong>follow-up</strong> abaixo — evita orçamento em limbo.
                Os valores já vem sugeridos; ajuste se quiser.
              </p>
              <Field>
                Próxima ação
                <Input
                  type="text"
                  value={proximaAcaoEnviar}
                  onChange={(e) => setProximaAcaoEnviar(e.target.value)}
                  placeholder="Ex: ligar quinta, enviar revisão..."
                  maxLength={200}
                  required
                  autoFocus
                />
              </Field>
              <Field>
                Data prevista
                <Input
                  type="date"
                  value={dataProximaAcaoEnviar}
                  onChange={(e) => setDataProximaAcaoEnviar(e.target.value)}
                  required
                />
                <InlineHint>
                  Sugestão: hoje + 3 dias úteis. Use o filtro de status temporal
                  na lista para acompanhar follow-ups vencidos.
                </InlineHint>
              </Field>

              {/* ADR-0050 — gates do motor DIVISOR no envio. Os blocos abaixo
                  aparecem quando o backend bloqueia (mensagem em acaoErro):
                  G13/Q7 viram checkbox de ciência que reenvia com a flag;
                  G9 ganha link para o estoque; G15 orienta a aprovação. */}
              {motorDivisor
                && (aceitarEstimativas
                  || confirmacaoPedida === "aceitar_estimativas_internas"
                  // Fallback: backend antigo citava o campo na mensagem.
                  || (acaoErro || "").includes("aceitar_estimativas_internas")) && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: "0.85rem" }}>
                  <input
                    type="checkbox"
                    checked={aceitarEstimativas}
                    onChange={(e) => setAceitarEstimativas(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    Estou ciente de que há <strong>cotação(ões) com valor
                    estimado</strong> (não firme) e quero enviar assim mesmo.
                    Para gerar contrato depois, elas precisarão estar firmes.
                  </span>
                </label>
              )}
              {motorDivisor
                && (confirmarVencidas
                  || confirmacaoPedida === "confirmar_cotacoes_vencidas"
                  // Fallback: backend antigo citava o campo na mensagem.
                  || (acaoErro || "").includes("confirmar_cotacoes_vencidas")) && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: "0.85rem" }}>
                  <input
                    type="checkbox"
                    checked={confirmarVencidas}
                    onChange={(e) => setConfirmarVencidas(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    Confirmo que o(s) valor(es) da(s) <strong>cotação(ões)
                    vencida(s)</strong> seguem válidos com o fornecedor
                    (confirmação auditada).
                  </span>
                </label>
              )}
              {motorDivisor && (acaoErro || "").includes("geometria") && (
                <InlineHint>
                  <Link to="/suprimentos/produtos">
                    Abrir cadastro de produtos do estoque
                  </Link>{" "}
                  para informar largura e comprimento da chapa — sem isso não
                  dá para contar chapas.
                </InlineHint>
              )}
              {motorDivisor
                && (acaoErro || "").includes("não cobre nem as obrigações") && (
                <InlineHint>
                  Ajuste o preço na <strong>Composição de preço</strong> do
                  cockpit, ou peça a um gestor com alçada para aprovar preço
                  abaixo do custo e salvar o preço com justificativa — só
                  depois disso o envio é liberado.
                </InlineHint>
              )}
            </>
          )}
          {confirmando === "aprovar" && (
            <p style={{ fontSize: "0.9rem" }}>
              Marca como <strong>APROVADO pelo cliente</strong>. Após isso, você pode converter em pedido.
              Ação reversível via recusa.
            </p>
          )}
          {confirmando === "recusar" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Marca como <strong>RECUSADO</strong>. Terminal — para nova proposta, criar novo orçamento.
              </p>
              <Textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Motivo da recusa (obrigatório)"
                required
                autoFocus
                rows={3}
              />
            </>
          )}
          {confirmando === "expirar" && (
            <p style={{ fontSize: "0.9rem" }}>
              Marca como <strong>EXPIRADO</strong>. Terminal — para retomar, criar novo orçamento.
            </p>
          )}
          {confirmando === "registrar-razao" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Categoriza por que este orçamento foi perdido. Alimenta o ranking
                de razões no Painel Comercial. Pode editar depois.
              </p>
              <Field>
                Razão
                <Select
                  value={razaoCodigo}
                  onChange={(e) => setRazaoCodigo(e.target.value)}
                  autoFocus
                >
                  {RAZOES_PERDA.map((r) => (
                    <option key={r.codigo} value={r.codigo}>{r.label}</option>
                  ))}
                </Select>
              </Field>
              <Textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Detalhe (opcional)"
                rows={2}
              />
            </>
          )}
          {confirmando === "excluir" && (
            <>
              <p style={{ fontSize: "0.95rem", color: "#9D2A22", fontWeight: 600 }}>
                Atenção: esta ação remove o orçamento <strong>{o.numero}</strong> permanentemente.
              </p>
              <p style={{ fontSize: "0.88rem" }}>
                Tudo associado também some: ambientes, itens, versões, anexos,
                links públicos. <strong>Auditoria mantém o registro</strong> de quem
                excluiu e quando.
              </p>
              <p style={{ fontSize: "0.85rem", color: "#666" }}>
                Em geral, preferir <strong>"Marcar expirado"</strong> ou
                <strong> "Recusar"</strong> — preservam historico para metricas. Use
                excluir apenas para orçamentos de teste ou criados por engano.
              </p>
              <InlineHint>
                Pode exigir confirmação de MFA (autenticação recente) para concluir.
              </InlineHint>
            </>
          )}
          {confirmando === "nova-versao" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Duplica os ambientes/itens da versão atual em uma nova versão (vN+1) e
                volta o orçamento para <strong>RASCUNHO</strong> para edição + re-envio.
              </p>
              <Textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Motivo da mudança (ex: cliente trocou bancada)"
                autoFocus
                rows={3}
              />
              <InlineHint>Aparecerá no histórico de versões para auditoria.</InlineHint>
            </>
          )}
          {confirmando === "iniciar-revisao" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Cria a <strong>nova versão</strong> (copia da atual; a versão anterior
                fica preservada) e abre para edicao. Ajuste os ambientes/itens e depois
                clique em <strong>Finalizar revisão</strong>; ao enviar/compartilhar,
                o {termoContato} recebe a nova versão.
              </p>
              {o.revisao_solicitada_motivo && (
                <InlineHint>
                  Pedido do {termoContato}: "{o.revisao_solicitada_motivo}"
                </InlineHint>
              )}
            </>
          )}
          {confirmando === "finalizar-revisao" && (
            <p style={{ fontSize: "0.9rem" }}>
              Fecha a revisão. A versão nova fica pronta — use
              <strong> "Compartilhar com {termoContato}"</strong> (ou Enviar) para
              mandar a nova versão.
            </p>
          )}
          {confirmando === "cancelar-revisao" && (
            <p style={{ fontSize: "0.9rem" }}>
              <strong>Descarta</strong> a revisão: apaga a versão nova (em edição) e
              volta a versão anterior como ativa. Esta ação não pode ser desfeita.
            </p>
          )}
          {confirmando === "converter" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Cria um <strong>pedido firmado</strong> a partir da versão ativa. Os ambientes
                e itens são copiados e viram o contrato; o orçamento passa a constar como
                convertido e não aceita mais edição.
              </p>
              <Textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Observações para o pedido (opcional)"
                autoFocus
                rows={3}
              />
            </>
          )}
          {confirmando === "aplicar-template" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Aplica o template no orçamento — ambientes e itens do template são
                <strong> adicionados</strong> aos existentes (não apaga os atuais).
                Se quiser substituir, apague os ambientes antes.
              </p>
              <Field>
                Template
                <Select
                  value={templateSel}
                  onChange={(e) => setTemplateSel(e.target.value)}
                  required
                >
                  {templatesDisponiveis.length === 0 && (
                    <option value="">Nenhum template ativo</option>
                  )}
                  {templatesDisponiveis.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome} ({t.qtd_ambientes} ambiente{t.qtd_ambientes !== 1 ? "s" : ""})
                    </option>
                  ))}
                </Select>
                <InlineHint>
                  Crie ou edite templates em <strong>/comercial/templates</strong>.
                </InlineHint>
              </Field>
            </>
          )}
          {confirmando === "salvar-como-template" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Cria um <strong>novo template</strong> copiando ambientes+itens da versão ativa.
                Reutilizável via "Aplicar template" em outros orçamentos.
              </p>
              <Field>
                Nome do template
                <Input
                  type="text"
                  value={nomeNovoTemplate}
                  onChange={(e) => setNomeNovoTemplate(e.target.value)}
                  placeholder="Ex: Cozinha padrão 3x4"
                  required
                  autoFocus
                  maxLength={120}
                />
              </Field>
              <Field>
                Descrição (opcional)
                <Textarea
                  value={descricaoNovoTemplate}
                  onChange={(e) => setDescricaoNovoTemplate(e.target.value)}
                  rows={2}
                  maxLength={300}
                />
              </Field>
            </>
          )}
          {confirmando === "compartilhar" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Gera um <strong>link único</strong> que o {termoContato} abre no celular/desktop sem
                login para visualizar e aprovar/pedir revisão do orçamento. A decisao do{" "}
                {termoContato} fica registrada com IP + data + hora (evidencia eletronica).
              </p>
              <Field>
                Validade do novo link (dias)
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={diasValidadeLink}
                  onChange={(e) => setDiasValidadeLink(e.target.value)}
                />
                <InlineHint>
                  Após esse prazo, o link expira automaticamente. Você pode revogar a qualquer momento.
                </InlineHint>
              </Field>

              {linkGeradoUrl && (
                <InfoMsg style={{ wordBreak: "break-all", alignItems: "flex-start" }}>
                  <FiCheck size={14} aria-hidden="true" />
                  <span>
                    Link gerado e copiado:{" "}
                    <a
                      href={linkGeradoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      {linkGeradoUrl}
                    </a>
                  </span>
                </InfoMsg>
              )}

              {linksPublicos.length > 0 && (
                <Block style={{ marginTop: 8 }}>
                  <SectionTitle>Links existentes ({linksPublicos.length})</SectionTitle>
                  {linksPublicos.map((link) => {
                    const ativo = link.utilizavel;
                    return (
                      <FieldRow key={link.id}>
                        <FieldLabel>
                          {ativo
                            ? `Ativo (${link.acessos_count} acessos)`
                            : link.revogado_em
                              ? "Revogado"
                              : link.expirado
                                ? "Expirado"
                                : "Bloqueado"}
                        </FieldLabel>
                        <FieldValue>
                          <div style={{ display: "flex", gap: 6 }}>
                            {ativo && (
                              <SecondaryButton
                                type="button"
                                onClick={() => handleCopiarLink(link.id)}
                                style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                              >
                                {copiado === link.id ? "Copiado!" : "Copiar link"}
                              </SecondaryButton>
                            )}
                            {ativo && (
                              <DangerButton
                                type="button"
                                onClick={() => handleRevogarLink(link.id)}
                                style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                              >
                                <FiTrash2 size={11} /> Revogar
                              </DangerButton>
                            )}
                          </div>
                          {link.ultimo_acesso_em && (
                            <InlineHint style={{ marginTop: 4 }}>
                              Último acesso: {fmtData(link.ultimo_acesso_em)}
                            </InlineHint>
                          )}
                        </FieldValue>
                      </FieldRow>
                    );
                  })}
                </Block>
              )}
            </>
          )}

          {acaoErro && (
            <InlineHint style={{ color: "#9D2A22" }}>
              <FiAlertCircle size={12} /> {acaoErro}
            </InlineHint>
          )}

          <FormFooter>
            <SecondaryButton type="button" onClick={fecharConfirmacao} disabled={salvando}>
              {confirmando === "compartilhar" ? "Fechar" : "Cancelar"}
            </SecondaryButton>
            {confirmando === "recusar" || confirmando === "expirar"
              || confirmando === "excluir" || confirmando === "cancelar-revisao" ? (
              <DangerButton type="submit" disabled={salvando}>
                {salvando ? "Aplicando..." :
                  confirmando === "excluir" ? "Excluir definitivamente" : "Confirmar"}
              </DangerButton>
            ) : (
              <PrimaryButton type="submit" disabled={salvando}>
                {salvando
                  ? (confirmando === "compartilhar" ? "Gerando..." : "Aplicando...")
                  : (confirmando === "compartilhar" ? "Gerar novo link" : "Confirmar")}
              </PrimaryButton>
            )}
          </FormFooter>
        </Form>
      </SlideOverPanel>

      {/* Bloco F.4: modal cadastrar email do cliente (antes de enviar) */}
      <SlideOverPanel
        open={cadastrandoEmailCliente}
        onClose={() => {
          setCadastrandoEmailCliente(false);
          setNovoEmailCliente("");
          setNovoTelefoneCliente("");
          setAcaoErro(null);
        }}
        title="Cadastrar email do cliente"
        subtitle={
          o
            ? `Cliente "${o.cliente_nome || ""}" sem email — necessário para enviar o orçamento`
            : ""
        }
        width="min(95vw, 480px)"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            salvarEmailCliente();
          }}
        >
          <Field>
            Email <span style={{ color: "#c00" }}>*</span>
            <Input
              type="email"
              value={novoEmailCliente}
              onChange={(e) => setNovoEmailCliente(e.target.value)}
              placeholder="cliente@exemplo.com"
              required
              autoFocus
            />
            <InlineHint>
              Obrigatório para enviar o orçamento (link publico vai pra este email).
            </InlineHint>
          </Field>
          <Field>
            Telefone (opcional)
            <Input
              type="tel"
              value={novoTelefoneCliente}
              onChange={(e) => setNovoTelefoneCliente(e.target.value)}
              placeholder="(11) 99999-9999"
            />
            <InlineHint>
              Útil para follow-up por WhatsApp/SMS depois do envio.
            </InlineHint>
          </Field>
          {acaoErro && <ErrorMsg>{acaoErro}</ErrorMsg>}
          <FormFooter>
            <SecondaryButton
              type="button"
              onClick={() => {
                setCadastrandoEmailCliente(false);
                setNovoEmailCliente("");
                setNovoTelefoneCliente("");
                setAcaoErro(null);
              }}
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton type="submit">
              Salvar e continuar
            </PrimaryButton>
          </FormFooter>
        </Form>
      </SlideOverPanel>

      {/* Fase 3: painel de diff entre versoes */}
      <SlideOverPanel
        open={Boolean(diffVersaoNum)}
        onClose={fecharDiff}
        title={`Diferenças — v${diffVersaoNum} vs v${(diffVersaoNum || 1) - 1}`}
        subtitle={o.numero}
        width="min(95vw, 560px)"
      >
        {diffLoading && <EmptyMsg>Calculando diferenças...</EmptyMsg>}
        {diffErro && <ErrorMsg>{diffErro}</ErrorMsg>}
        {diffData && !diffLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {!diffData.tem_anterior ? (
              <InfoMsg>
                <FiGitBranch size={14} aria-hidden="true" />
                Primeira versão — não há versão anterior para comparar.
              </InfoMsg>
            ) : (
              <>
                <div className="flex" style={{ gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <FieldLabel>Variação de valor</FieldLabel>
                    <div style={{
                      fontWeight: 700, fontSize: 18,
                      color: (diffData.delta_valor || 0) >= 0 ? "var(--success-text)" : "var(--danger-text)",
                    }}>
                      {(diffData.delta_valor || 0) >= 0 ? "+" : ""}{fmtMoeda(diffData.delta_valor)}
                    </div>
                    <InlineHint>
                      {fmtMoeda(diffData.valor_anterior)} → {fmtMoeda(diffData.valor_atual)}
                    </InlineHint>
                  </div>
                </div>

                <div className="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                  {diffData.resumo.ambientes_adicionados > 0 && (
                    <Pill $tone="success">+{diffData.resumo.ambientes_adicionados} ambiente(s)</Pill>
                  )}
                  {diffData.resumo.ambientes_removidos > 0 && (
                    <Pill $tone="danger">−{diffData.resumo.ambientes_removidos} ambiente(s)</Pill>
                  )}
                  {diffData.resumo.itens_adicionados > 0 && (
                    <Pill $tone="success">+{diffData.resumo.itens_adicionados} item(ns)</Pill>
                  )}
                  {diffData.resumo.itens_removidos > 0 && (
                    <Pill $tone="danger">−{diffData.resumo.itens_removidos} item(ns)</Pill>
                  )}
                  {diffData.resumo.itens_alterados > 0 && (
                    <Pill $tone="warning">~{diffData.resumo.itens_alterados} alterado(s)</Pill>
                  )}
                  {diffData.ambientes.length === 0 && (
                    <Pill $tone="neutral">Sem mudanças nos ambientes/itens</Pill>
                  )}
                </div>

                {diffData.ambientes.map((amb, i) => (
                  <Block key={i}>
                    <SectionTitle>
                      {amb.nome}{" "}
                      <Pill
                        $tone={amb.status === "adicionado" ? "success" : amb.status === "removido" ? "danger" : "warning"}
                        style={{ marginLeft: 6 }}
                      >
                        {amb.status}
                      </Pill>
                    </SectionTitle>
                    {amb.itens_adicionados.map((it, j) => (
                      <FieldRow key={`a${j}`}>
                        <FieldLabel style={{ color: "var(--success-text)" }}>+ adicionado</FieldLabel>
                        <FieldValue>{it.descricao} — {it.quantidade} × {fmtMoeda(it.valor_unitario)}</FieldValue>
                      </FieldRow>
                    ))}
                    {amb.itens_removidos.map((it, j) => (
                      <FieldRow key={`r${j}`}>
                        <FieldLabel style={{ color: "var(--danger-text)" }}>− removido</FieldLabel>
                        <FieldValue style={{ textDecoration: "line-through", opacity: 0.7 }}>
                          {it.descricao} — {it.quantidade} × {fmtMoeda(it.valor_unitario)}
                        </FieldValue>
                      </FieldRow>
                    ))}
                    {amb.itens_alterados.map((it, j) => (
                      <FieldRow key={`m${j}`}>
                        <FieldLabel style={{ color: "var(--warning-text)" }}>~ {it.descricao}</FieldLabel>
                        <FieldValue>
                          <span style={{ opacity: 0.7 }}>{it.de.quantidade} × {fmtMoeda(it.de.valor_unitario)}</span>
                          {" → "}
                          <strong>{it.para.quantidade} × {fmtMoeda(it.para.valor_unitario)}</strong>
                        </FieldValue>
                      </FieldRow>
                    ))}
                  </Block>
                ))}
              </>
            )}
          </div>
        )}
      </SlideOverPanel>

      {/* Orcamento v2: slide-over de import 3D (fontes reais via CADIntegration) */}
      <Importar3DPanel
        open={import3DAberto}
        onClose={() => setImport3DAberto(false)}
        orcamento={o}
        onImported={recarregar}
      />
    </PageContainer>
  );
}

export default OrcamentoDetalhe;
